import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
// @ts-ignore
import { ACTUAL_THEMES_ROOT_ID, SUBJECTS_SLUGS } from '@yandex-int/k-common/constants';
import { Team, Theme } from '@yandex-int/k-common/typings';
import { keyBy } from 'lodash';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

import { getCurrentClass } from 'common.components/user/selectors';
import { doesThemeExist } from 'store/themes/utils';
import {
    courseIdSelector,
    getThemeIdFromProps,
    getSubjectFromProps,
    PropsWithGrade,
    PropsWithSubject,
} from 'utils/selectors/selectors';

import { StoreState } from './typings';

const themesStoreSelector = (storeState: any): StoreState => storeState.themes;

const getGradeFromProps = (_storeState: any, ownProps: PropsWithGrade): number | null => {
    if (ownProps && ownProps?.grade) {
        const grade = Number(ownProps.grade);
        return Number.isNaN(grade) ? null : grade;
    }

    return null;
};

export const getThemes = createCachedSelector(
    [themesStoreSelector, getSubjectFromProps, getGradeFromProps],
    (state, subject, grade): Array<Theme> => {
        if (!subject) {
            return [];
        }

        const themes = state?.themes[subject]?.themes || [];

        if (!grade) {
            return themes;
        }

        return themes.filter((theme) => theme.grades.some((themeGrade) => themeGrade.grade === grade));
    }
)((state, props: PropsWithSubject & PropsWithGrade) => {
    return `${props.subject}:${props.grade}`;
});

export const getRootThemes = createSelector(
    [getThemes],
    (themes: Array<Theme>): Array<Theme> => themes.filter((theme) => !theme.parent)
);

export const getPlainThemes = createSelector(
    [getRootThemes],
    (themes): Array<Theme> => themes.filter((theme) => !theme.promoted)
);

export const getPromotedThemes = createSelector(
    [getRootThemes],
    (themes): Array<Theme> => themes.filter((theme) => theme.promoted)
);

export const getFirstPlainThemeId = createSelector([getPlainThemes], (themes): number | null => themes[0]?.id || null);

export const getThemesDictionary = createSelector(
    [getThemes],
    (themes: Array<Theme>): Record<number, Theme> => keyBy(themes, 'id')
);

export const getRootThemesLoadingStatus = createSelector(
    [themesStoreSelector, getSubjectFromProps],
    (state: StoreState, subject: SUBJECTS_SLUGS): LOADING_STATUSES =>
        state?.themes[subject]?.rootThemesLoadingStatus || LOADING_STATUSES.UNSENT
);

export const getRootThemeByListTheme = createSelector(
    [getThemesDictionary, getThemeIdFromProps],
    (themes: Record<number, Theme>, themeId): Theme | undefined => {
        if (!themeId) {
            return undefined;
        }

        let theme = themes[themeId];

        while (theme && theme.parent) {
            theme = themes[theme.parent];
        }

        return theme;
    }
);

export const getShouldHideChildThemes = createSelector([getRootThemeByListTheme], (theme): boolean => {
    return theme?.should_hide_child_themes || false;
});

export const getThemeById = createSelector(
    [getThemesDictionary, getThemeIdFromProps],
    (themes: Record<number, Theme>, themeId): Theme | undefined => {
        if (!themeId) {
            return undefined;
        }

        return themes[themeId];
    }
);

export const getClosestParent = createSelector([getThemeById, getThemes], (theme, themes): Theme | null => {
    if (!theme?.parent) {
        return null;
    }

    return themes.find(({ id }) => id === theme.parent) || null;
});

export const getThemesTreeLoadingStatus = createSelector(
    [themesStoreSelector, getSubjectFromProps, getThemeIdFromProps, getRootThemeByListTheme],
    (state: StoreState, subject: SUBJECTS_SLUGS, themeId: number, rootTheme: Theme | undefined): LOADING_STATUSES => {
        const subjectsThemes = state?.themes[subject];

        if (!themeId || !subjectsThemes) {
            return LOADING_STATUSES.UNSENT;
        }

        return (
            subjectsThemes.themesTreeLoadingStatus[themeId] ||
            (rootTheme && subjectsThemes.themesTreeLoadingStatus[rootTheme.id]) ||
            LOADING_STATUSES.UNSENT
        );
    }
);

export const getChildThemes = createSelector(
    [getThemes, getThemeIdFromProps],
    (themes: Array<Theme>, rootThemeId): Array<Theme> => {
        if (!rootThemeId) {
            return [];
        }

        return themes.filter((theme) => theme.parent === rootThemeId);
    }
);

const getChildrenThemes = (themes: Array<Theme>, parentThemeId: number | null): Array<TreeItem<Theme>> => {
    return themes
        .filter((t) => t.parent === parentThemeId)
        .map((t) => new TreeItem(t, getChildrenThemes(themes, t.id)));
};

// rootThemeId is a result of the call of getRootThemeByListTheme selector
export const getThemesTree = createSelector(
    [getThemes, getThemeIdFromProps],
    (themes: Array<Theme>, rootThemeId): Array<TreeItem<Theme>> => {
        if (!rootThemeId) {
            return [];
        }

        return getChildrenThemes(themes, rootThemeId);
    }
);

export const getActualThemeSettings = createSelector(
    [getCurrentClass, getSubjectFromProps],
    (team: Team | null, subject: SUBJECTS_SLUGS): ActualThemesSettings | null => {
        if (isSSR || team === null) {
            return null;
        }

        const actualThemesSettings = window._data.actualThemesSettings.actualThemes || [];

        return (
            actualThemesSettings.find((settings: ActualThemesSettings) => {
                return settings.grade === team.level && settings.subject === subject;
            }) || null
        );
    }
);

export const getShouldShowActualThemes = createSelector(
    [getActualThemeSettings],
    (settings: ActualThemesSettings | null): boolean => settings !== null
);

export const getActualThemesDescription = createSelector(
    [getActualThemeSettings],
    (settings: ActualThemesSettings | null): string | null => settings?.description || null
);

export const getActualThemesLoadingStatus = createSelector(
    [themesStoreSelector, getSubjectFromProps, courseIdSelector],
    (state: StoreState, subject: SUBJECTS_SLUGS, courseId): LOADING_STATUSES => {
        const subjectsThemes = state?.themes[subject];

        if (!courseId || !subjectsThemes) {
            return LOADING_STATUSES.UNSENT;
        }

        return subjectsThemes.actualThemesLoadingStatus[courseId] || LOADING_STATUSES.UNSENT;
    }
);

export const getShouldLoadActualThemes = createSelector(
    [getShouldShowActualThemes, getActualThemesLoadingStatus],
    (shouldShow: boolean, loadingStatus: LOADING_STATUSES): boolean => {
        return (shouldShow && loadingStatus === LOADING_STATUSES.UNSENT) || loadingStatus === LOADING_STATUSES.ERROR;
    }
);

export const getActualThemes = createSelector(
    [themesStoreSelector, getSubjectFromProps, courseIdSelector],
    (state: StoreState, subject: SUBJECTS_SLUGS, courseId): Array<Theme> => {
        const subjectsThemes = state?.themes[subject];

        if (!courseId || !subjectsThemes) {
            return [];
        }

        return subjectsThemes.actualThemes[courseId] || [];
    }
);

export const getActualThemesTreeByCourseId = createSelector(
    [getActualThemes],
    (themes: Array<Theme>): Array<TreeItem<Theme>> => {
        const rootTheme = themes.find((theme) => theme.level === 0);
        const actualThemesTree = getChildrenThemes(themes, rootTheme?.id || ACTUAL_THEMES_ROOT_ID);

        return actualThemesTree || [];
    }
);

export const getActualChildThemes = createSelector(
    [getActualThemes, getThemeIdFromProps],
    (themes: Array<Theme>, rootThemeId): Array<Theme> => {
        if (!rootThemeId) {
            return [];
        }

        return themes.filter((theme) => theme.parent === rootThemeId);
    }
);

export const getIsActualTheme = createSelector([getThemeIdFromProps], (themeId): boolean => {
    if (!themeId) {
        return false;
    }

    return themeId >= ACTUAL_THEMES_ROOT_ID;
});

export const getDoesThemeExist = createSelector([getThemes, getThemeIdFromProps], (themes, themeId): boolean => {
    if (!themeId) {
        return false;
    }

    return doesThemeExist(themeId, themes);
});

class TreeItem<T> {
    children: Array<TreeItem<T>>;
    item: T;

    constructor(item: T, children: Array<TreeItem<T>>) {
        this.children = children;
        this.item = item;
    }
}
